import React, { PureComponent } from 'react';
import classnames from 'classnames';
import { isNil, truncate } from 'lodash';

import SVGIcon from 'components/svg-icon/SVGIcon';
import SVGDynamicIcon from 'components/svg-dynamic-icon/SVGDynamicIcon';
import GalleryModal from 'components/gallery-modal/gallery-modal/GalleryModal';
import { withModal } from 'components/modal/ModalHOC';
import ConsentPlaceholder from 'components/consent-placeholder/ConsentPlaceholder';

import InfluencerPostContext from '../context/InfluencerPostContext';

import { abbrNum } from 'utils/helper/common';
import {
  CHANNEL_TYPE_TIKTOK,
  CHANNEL_TYPE_YOUTUBE,
  getTiktokEmbedUrl,
  getYoutubeEmbedUrl,
  getYoutubeVideoThumbnail
} from 'utils/channels';
import { isValidAssetUrl } from 'utils/url';
import { WITH_GALLERY_MODAL_CONFIG } from 'utils/gallery';

import type { Post } from 'types';
import type { ConsentReducerSupportedChannels } from 'redux-store/types/ConsentReducer.type';

const TITLE_LENGTH = 50;

const getAssetUrl = (channel: string, url: string) => {
  switch (channel) {
    case CHANNEL_TYPE_TIKTOK:
      return { embedUrl: getTiktokEmbedUrl(url) };
    case CHANNEL_TYPE_YOUTUBE:
      return { embedUrl: getYoutubeEmbedUrl(url) };
    default:
      return {};
  }
};

const LinkWithModal = withModal(<a />, {
  ...WITH_GALLERY_MODAL_CONFIG,
  modalContent: (props) => (
    <InfluencerPostContext.Consumer>
      {(results: Array<Post>) => <GalleryModal
        {...props}
        items={results.map((result) => ({
          ...result,
          type: result.channel,
          ...(getAssetUrl(result.channel, result.url))
        }))}
      />}
    </InfluencerPostContext.Consumer>
  )
});

type Props = {
  numFollowers: number,
  post: Post,
  size: string,
  type: string,
  style: string,
  index: number,
  hasConsent: boolean
}

class InfluencerPost extends PureComponent<Props> {
  static defaultProps = {
    style: 'normal'
  };

  renderPlaceholder(post: Post, type: ConsentReducerSupportedChannels, imageClasses: string) {
    return (
      <div className={imageClasses}>
        <ConsentPlaceholder
          withLearnMore
          type={type}
          link={post.url}
        />
      </div>
    );
  }

  renderInstagram(post, postClasses, postDataClasses, imageClasses) {
    const { numFollowers, index, hasConsent } = this.props;
    const engagement = ((post.numLikes + post.numComments) / numFollowers) * 100;
    return (
      <div
        className={postClasses}
        data-qa="influencer-post-instagram"
      >
        {!hasConsent && (
          this.renderPlaceholder(post, 'instagram', imageClasses)
        )}
        {hasConsent && (
          <LinkWithModal
            modalContentProps={{
              theme: 'white',
              currentPosition: index,
              className: 'influncer-post__modal'
            }}
            className={`${imageClasses} lazyload`}
            href={post.assetUrl}
            style={{
              backgroundImage: `url('${post.assetUrl}')`
            }}
          />
        )}
        <div className={postDataClasses}>
          <div className="post__values">
            <span className="post__value-icon">
              <SVGDynamicIcon
                name="heart-custom-fg"
                width={12}
                height={12}
                customColor="grey"
              />
            </span>
            <span>{abbrNum(post.numLikes, { decimal: 1 })}</span>
          </div>
          <div className="post__values">
            <span className="post__value-icon">
              <SVGDynamicIcon
                name="speech-bubble-custom-fg"
                width={12}
                height={12}
                customColor="grey"
              />
            </span>
            <span>{abbrNum(post.numComments, { decimal: 1 })}</span>
          </div>
          {
            Boolean(numFollowers) && (
              <div className="post__values">
                <span className="post__value-icon">
                  <SVGDynamicIcon
                    name="rotation-arrows-custom-fg"
                    width={11}
                    height={12}
                    customColor="grey"
                  />
                </span>
                <span>{`${engagement.toFixed(1)}%`}</span>
              </div>
            )
          }
        </div>
      </div>
    );
  }

  renderYoutube(post, postClasses, postDataClasses, imageClasses, size) {
    const { numFollowers, index, hasConsent } = this.props;

    return (
      <div className={postClasses}>
        {!hasConsent && (
          this.renderPlaceholder(post, 'youtube', imageClasses)
        )}
        {hasConsent && (
          <LinkWithModal
            modalContentProps={{
              theme: 'white',
              currentPosition: index,
              className: 'influncer-post__modal'
            }}
            className={`${imageClasses} lazyload`}
            href={getYoutubeVideoThumbnail(post.url)}
            style={{
              backgroundImage: `url('${post.imageUrl || getYoutubeVideoThumbnail(post.url)}')`
            }}
          >
            <SVGIcon
              name="youtube-play"
              width={size === 'small' ? 32 : 54}
              height={size === 'small' ? 32 : 54}
            />
          </LinkWithModal>
        )}
        {
          isNil(numFollowers) && (
            <div className="post__title">
              <span>{truncate(post.title, { length: TITLE_LENGTH })}</span>
            </div>
          )
        }
        {size === 'bigger' && (
          <div className={postDataClasses}>
            <div className="post__values">
              <span className="post__value-icon">
                <SVGDynamicIcon
                  name="eye-custom-fg"
                  width={16}
                  height={16}
                  customColor="grey"
                />
              </span>
              <span>{abbrNum(post.numViews, { decimal: 1 })}</span>
            </div>
            <div className="post__values">
              <span className="post__value-icon">
                <SVGDynamicIcon
                  name="thumbs-up-custom-fg"
                  width={16}
                  height={16}
                  customColor="grey"
                />
              </span>
              <span>{abbrNum(post.numLikes, { decimal: 1 })}</span>
            </div>
            <div className="post__values">
              <span className="post__value-icon">
                <SVGDynamicIcon
                  name="speech-bubble-custom-fg"
                  width={16}
                  height={16}
                  customColor="grey"
                />
              </span>
              <span>{abbrNum(post.numComments, { decimal: 1 })}</span>
            </div>
          </div>
        )}
      </div>
    );
  }

  renderTikTok(post, postClasses, postDataClasses, imageClasses, size) {
    const { numFollowers, index, hasConsent } = this.props;

    return (
      <div className={postClasses}>
        {!hasConsent && (
          this.renderPlaceholder(post, 'tiktok', imageClasses)
        )}
        {hasConsent && (
          <LinkWithModal
            modalContentProps={{
              theme: 'white',
              currentPosition: index,
              className: 'influncer-post__modal'
            }}
            className={`${imageClasses} lazyload`}
            href={post.url}
            style={{
              backgroundImage: `url('${post.assetUrl}')`
            }}
          >
            <SVGIcon
              name="play-button"
              width={size === 'small' ? 32 : 54}
              height={size === 'small' ? 32 : 54}
            />
          </LinkWithModal>
        )}
        {
          isNil(numFollowers) && (
            <div className="post__title">
              <span>{truncate(post.title, { length: TITLE_LENGTH })}</span>
            </div>
          )
        }
        {size === 'bigger' && (
          <div className={postDataClasses}>
            <div className="post__values">
              <span className="post__value-icon">
                <SVGDynamicIcon
                  name="heart-custom-fg"
                  width={16}
                  height={16}
                  customColor="grey"
                />
              </span>
              <span>{abbrNum(post.numLikes, { decimal: 1 })}</span>
            </div>
            <div className="post__values">
              <span className="post__value-icon">
                <SVGDynamicIcon
                  name="speech-bubble-custom-fg"
                  width={16}
                  height={16}
                  customColor="grey"
                />
              </span>
              <span>{abbrNum(post.numComments, { decimal: 1 })}</span>
            </div>
            <div className="post__values">
              <span className="post__value-icon">
                <SVGDynamicIcon
                  name="eye-custom-fg"
                  width={16}
                  height={16}
                  customColor="grey"
                />
              </span>
              <span>{abbrNum(post.numViews, { decimal: 1 })}</span>
            </div>
            <div className="post__values">
              <span className="post__value-icon">
                <SVGDynamicIcon
                  name="arrow-curved-custom-fg"
                  width={16}
                  height={16}
                  customColor="grey"
                />
              </span>
              <span>{abbrNum(post.numShares, { decimal: 1 })}</span>
            </div>
          </div>
        )}
      </div>
    );
  }

  render() {
    const { post, size, style, type } = this.props;

    const postClasses = classnames({
      post: true,
      [`post--size-${size}`]: size,
      [`post--type-${type}`]: type,
      [`post--style-${style}`]: style
    });

    const imageClasses = classnames({
      post__image: true,
      [`post__image--size-${size}`]: size,
      [`post__image--type-${type}`]: type,
      [`post__image--style-${style}`]: style
    });

    const postDataClasses = classnames({
      post__data: true,
      [`post__data--style-${style}`]: style
    });

    if (isValidAssetUrl(post.url)) {
      switch (type) {
        case 'instagram':
          return this.renderInstagram(post, postClasses, postDataClasses, imageClasses);
        case 'youtube':
          return this.renderYoutube(post, postClasses, postDataClasses, imageClasses, size);
        case 'tiktok':
          return this.renderTikTok(post, postClasses, postDataClasses, imageClasses, size);
        default:
          return null;
      }
    }

    return null;
  }
}

export default InfluencerPost;
